// PII sanitization for the MN-4800 transfer_upload_* event payloads. The
// CSO scan flagged two fields that the new events forward to Mixpanel /
// Clarity:
//   - error_message: server-internal stack traces, absolute paths,
//     occasionally email addresses or session ids.
//   - file_name: customer-uploaded file names that can include first/last
//     names, project codes, business identifiers.
//
// These rules apply to the NEW events only (legacy `image_upload_*`
// dashboards already query raw values; changing those mid-flight would
// break running queries). When the parallel-emission window sunsets and
// the legacy events are removed, sanitization can move into the legacy
// pipeline too.

const MAX_ERROR_MESSAGE_LENGTH = 512;
const MAX_FILE_BASENAME_LENGTH = 24;

const EMAIL_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
// Matches absolute paths on macOS, Linux, and Windows.
const PATH_PATTERN = /(?:[a-z]:[\\/]|\/(?:Users|home|var|tmp|opt)\/)[^\s)'"]*/gi;
// Stack frames: `    at <fn> (<file>:<line>:<col>)` and similar.
const STACK_FRAME_PATTERN = /^\s*at\s+.+$/gim;

const REPLACEMENT = "[redacted]";

// Strips emails, absolute paths, and stack-frame lines from a free-text
// error message; truncates the result so server-side blow-ups don't ship
// 50KB stack traces to Mixpanel.
//
// @param {unknown} msg
// @returns {unknown} a sanitized string for string inputs, original value otherwise
export const sanitizeErrorMessage = msg => {
  if (typeof msg !== "string" || msg.length === 0) return msg;
  const stripped = msg
    .replace(STACK_FRAME_PATTERN, REPLACEMENT)
    .replace(EMAIL_PATTERN, REPLACEMENT)
    .replace(PATH_PATTERN, REPLACEMENT);
  if (stripped.length <= MAX_ERROR_MESSAGE_LENGTH) return stripped;
  return `${stripped.slice(0, MAX_ERROR_MESSAGE_LENGTH)}…[truncated]`;
};

// Fast non-cryptographic 32-bit hash (FNV-1a). Browser-safe, no crypto API
// needed, deterministic across browsers and environments. Sufficient for
// generating stable bucket tokens for analytics. Bitwise ops are required
// by the FNV-1a algorithm itself.
/* eslint-disable no-bitwise */
const fnv1a = str => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
};
/* eslint-enable no-bitwise */

// Replaces the customer-provided basename with a short digest while keeping
// the extension and a length signal. Preserves debugging utility (which
// extension, how long was it) without leaking the customer's file naming.
//
// "Personal Photo - Smith.png" → "p_8a7b2f4c__26.png"
// "art.png"                    → "a_5b3d1f9c__7.png" (under threshold,
//                                                     still hashed for
//                                                     consistency)
//
// @param {unknown} fileName
// @returns {unknown} sanitized string for string inputs, original value otherwise
export const sanitizeFileName = fileName => {
  if (typeof fileName !== "string" || fileName.length === 0) return fileName;

  const lastDot = fileName.lastIndexOf(".");
  const hasExtension = lastDot > 0 && lastDot < fileName.length - 1;
  const basename = hasExtension ? fileName.slice(0, lastDot) : fileName;
  const extension = hasExtension ? fileName.slice(lastDot + 1).toLowerCase() : "";

  // Keep only the first character (lowercased) of the basename plus the
  // FNV-1a digest, joined with the original length. The first character is
  // intentionally low-entropy so it doesn't function as a unique identifier.
  const firstChar = basename[0]?.toLowerCase() || "_";
  const digest = fnv1a(basename);
  const lengthSignal = basename.length;

  const safeBasename = `${firstChar}_${digest}__${lengthSignal}`.slice(0, MAX_FILE_BASENAME_LENGTH);
  return hasExtension ? `${safeBasename}.${extension}` : safeBasename;
};
